import type { MarketSourcePlatform } from './index';

export const collectionCategories = ['ad', 'post', 'product', 'comment', 'search', 'other'] as const;
export type CollectionCategory = typeof collectionCategories[number];
interface CollectionMetadata {
    schema_version: 'market-collection-overview/v1';
    workspace_id: string;
    scope: 'current_readable_since_enable';
    statistics_since: string;
    as_of: string;
    time_zone: string;
}
export interface MarketCollectionOverview extends CollectionMetadata {
    total: number;
    recent: number;
    recent_monitor_count: number;
    coverage: { monitors: number; ready_monitors: number };
    buckets: { platform: MarketSourcePlatform; category: CollectionCategory; day: string | null; count: number }[];
}
export interface MarketCollectionContents extends CollectionMetadata {
    total: number;
    next_cursor: string | null;
    items: { content_id: string; platform: MarketSourcePlatform; category: CollectionCategory; title: string; first_collected_at: string; monitor_ids: string[] }[];
}
export interface CollectionContentFilter {
    category?: CollectionCategory;
    platform?: MarketSourcePlatform;
    day?: string;
    recent?: boolean;
}

const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const count = (value: unknown) => Number.isSafeInteger(value) && Number(value) >= 0;
const timestamp = (value: unknown): value is string => typeof value === 'string' && Number.isFinite(Date.parse(value));
const category = (value: unknown) => collectionCategories.includes(value as CollectionCategory);
const platform = (value: unknown) => ['meta', 'reddit', 'x', 'youtube', 'facebook', 'instagram', 'amazon', 'google_search', 'google_trends', 'tiktok'].includes(String(value));
function metadata(value: unknown, workspace: string, zone: string): asserts value is Record<string, unknown> {
    if (!object(value) || value.schema_version !== 'market-collection-overview/v1' || value.workspace_id !== workspace
        || value.scope !== 'current_readable_since_enable' || value.time_zone !== zone
        || !timestamp(value.statistics_since) || !timestamp(value.as_of)) throw new Error('Invalid collection statistics response');
}
export function validateCollectionOverview(value: unknown, workspace: string, zone: string): MarketCollectionOverview {
    metadata(value, workspace, zone);
    if (!count(value.total) || !count(value.recent) || !count(value.recent_monitor_count) || !object(value.coverage)
        || !count(value.coverage.monitors) || !count(value.coverage.ready_monitors)
        || Number(value.coverage.ready_monitors) > Number(value.coverage.monitors)
        || !Array.isArray(value.buckets) || value.buckets.some(row => !object(row) || !platform(row.platform) || !category(row.category)
            || !count(row.count) || !(row.day === null || typeof row.day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(row.day))))
        throw new Error('Invalid collection statistics response');
    if (value.buckets.reduce((sum, row) => sum + row.count, 0) !== value.total
        || value.buckets.reduce((sum, row) => sum + (row.day === null ? 0 : row.count), 0) !== value.recent)
        throw new Error('Collection statistics do not reconcile');
    return value as unknown as MarketCollectionOverview;
}
export function validateCollectionContents(value: unknown, workspace: string, zone: string, asOf: string, cursor: string | null): MarketCollectionContents {
    metadata(value, workspace, zone);
    if (value.as_of !== asOf || !count(value.total) || !(value.next_cursor === null || typeof value.next_cursor === 'string' && value.next_cursor.length > 0 && value.next_cursor !== cursor)
        || !Array.isArray(value.items) || value.items.length > 30 || value.items.length > Number(value.total)
        || value.items.some(row => !object(row) || typeof row.content_id !== 'string' || !row.content_id
            || !platform(row.platform) || !category(row.category) || typeof row.title !== 'string'
            || !timestamp(row.first_collected_at) || !Array.isArray(row.monitor_ids) || row.monitor_ids.some(id => typeof id !== 'string')))
        throw new Error('Invalid collection contents response');
    return value as unknown as MarketCollectionContents;
}
